<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  records: {
    type: Array,
    default: () => [],
  },
  knowledgebaseId: {
    type: Number,
    default: null,
  },
  featuredRecordId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['upload', 'delete', 'set-featured']);

const router = useRouter();
const { t } = useI18n();

const featuredId = computed(() =>
  props.featuredRecordId != null && props.featuredRecordId !== ''
    ? String(props.featuredRecordId)
    : null
);

const isFeatured = record => featuredId.value === String(record.id);

const navigateToGallery = () => {
  if (props.knowledgebaseId) {
    router.push({
      name: 'settings_knowledgebases_content',
      params: { knowledgebaseId: props.knowledgebaseId },
    });
  }
};

const handleDelete = record => {
  emit('delete', record);
};

const handleSetFeatured = record => {
  emit('set-featured', record.id);
};
</script>

<template>
  <div>
    <div class="flex items-center justify-end mb-6">
      <Button
        icon="i-lucide-upload"
        variant="ghost"
        size="sm"
        @click="emit('upload')"
      >
        {{ t('SETTINGS.SPACES.GALLERY.UPLOAD') }}
      </Button>
      <Button
        icon="i-lucide-external-link"
        variant="ghost"
        size="sm"
        @click="navigateToGallery"
      >
        {{ t('SETTINGS.SPACES.GALLERY.VIEW_ALL') }}
      </Button>
    </div>

    <div v-if="records.length === 0" class="py-12 text-center">
      <Icon
        icon="i-lucide-image-off"
        class="size-12 mx-auto text-woot-75 mb-2"
      />
      <p class="text-sm text-slate-500 font-semibold">
        {{ t('SETTINGS.SPACES.GALLERY.EMPTY') }}
      </p>
    </div>

    <div v-else class="grid grid-cols-3 gap-2 max-h-[320px] overflow-y-auto">
      <div
        v-for="record in records.slice(0, 8)"
        :key="record.id"
        class="group relative aspect-square rounded-lg border border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors overflow-hidden bg-slate-100 dark:bg-slate-900"
        :class="{
          'ring-2 ring-woot-500 border-woot-500': isFeatured(record),
        }"
      >
        <div class="w-full h-full cursor-pointer" @click="navigateToGallery">
          <img
            v-if="record.file?.url"
            :src="record.file.url"
            :alt="record.name || t('SETTINGS.SPACES.GALLERY.IMAGE_ALT')"
            class="w-full h-full object-cover"
          />
          <div v-else class="w-full h-full flex items-center justify-center">
            <Icon icon="i-lucide-image" class="size-8 text-slate-400" />
          </div>
        </div>
        <span
          v-if="isFeatured(record)"
          class="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-woot-500 text-white"
        >
          {{ t('SETTINGS.SPACES.GALLERY.FEATURED') }}
        </span>
        <div
          class="absolute flex gap-1 top-2 right-2 opacity-0 group-hover:opacity-100 transition-all"
        >
          <button
            v-if="!isFeatured(record)"
            class="flex p-1.5 text-white bg-slate-900/50 hover:bg-woot-600 rounded-lg backdrop-blur-sm w-10 h-10 justify-center items-center"
            :title="t('SETTINGS.SPACES.GALLERY.SET_AS_COVER')"
            @click.stop="handleSetFeatured(record)"
          >
            <Icon icon="i-lucide-image" class="size-4" />
          </button>
          <button
            class="flex p-1.5 text-white bg-slate-900/50 hover:bg-red-600 rounded-lg backdrop-blur-sm w-10 h-10 justify-center items-center"
            :title="t('SETTINGS.SPACES.GALLERY.DELETE')"
            @click.stop="handleDelete(record)"
          >
            <Icon icon="i-lucide-trash-2" class="size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
